import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CatalogImportConfirmInput,
  CatalogImportConfirmResult,
  CatalogImportJob,
  CollectionMeta,
  CreateProductServiceInput,
  ImportCommitResult,
  ImportDuplicateAction,
  ImportPreviewResult,
  ListProductsServicesQuery,
  ProductServiceCounts,
  ProductServiceResource,
  UpdateProductServiceInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';
import { pollInterval } from '../realtime/live-updates';

const KEY = ['products-services'] as const;
const CATALOG_IMPORT_JOBS_KEY = ['products-services', 'ai-import-jobs'] as const;
/** While any job is still PENDING/PROCESSING, poll the ongoing-imports panel this often. */
const CATALOG_IMPORT_JOB_POLL_MS = 4_000;

export interface ProductServiceListPage {
  items: ProductServiceResource[];
  meta: CollectionMeta;
}

function toQuery(q: ListProductsServicesQuery): Record<string, string | number | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    kind: q.kind,
    status: q.status,
    name: q.name || undefined,
    sku: q.sku || undefined,
    unit: q.unit || undefined,
    priceMin: q.priceMin,
    priceMax: q.priceMax,
    category: q.category || undefined,
    inWhatsAppCatalog: q.inWhatsAppCatalog === undefined ? undefined : String(q.inWhatsAppCatalog),
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listProductsServices(
  q: ListProductsServicesQuery = {},
): Promise<ProductServiceListPage> {
  const res = await apiClient.getCollection<ProductServiceResource>('/products-services', {
    query: toQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getProductService = (id: string) =>
  apiClient.get<ProductServiceResource>(`/products-services/${id}`);
export const createProductService = (input: CreateProductServiceInput) =>
  apiClient.post<ProductServiceResource>('/products-services', input);
export const updateProductService = (id: string, patch: UpdateProductServiceInput) =>
  apiClient.patch<ProductServiceResource>(`/products-services/${id}`, patch);
export const deleteProductService = (id: string) =>
  apiClient.delete<void>(`/products-services/${id}`);
/** Restores an ARCHIVED product/service to ACTIVE - the general update endpoint already supports any status transition, this just names the one-field patch. */
export const unarchiveProductService = (item: Pick<ProductServiceResource, 'id' | 'kind'>) =>
  apiClient.patch<ProductServiceResource>(`/products-services/${item.id}`, {
    kind: item.kind,
    status: 'ACTIVE',
  });

/** Downloads the bulk-import .xlsx template and triggers the browser's save-file flow. */
export async function downloadImportTemplate(): Promise<void> {
  const { blob, fileName } = await apiClient.getBlob('/products-services/import/template');
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? 'products-services-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const previewProductServiceImport = (file: File) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiClient.post<ImportPreviewResult>('/products-services/import/preview', form);
};

export const commitProductServiceImport = (file: File, duplicateAction: ImportDuplicateAction) => {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('duplicateAction', duplicateAction);
  return apiClient.post<ImportCommitResult>('/products-services/import', form);
};

export function usePreviewProductServiceImport() {
  return useMutation<ImportPreviewResult, ApiClientError, File>({
    mutationFn: previewProductServiceImport,
  });
}

export function useCommitProductServiceImport() {
  const qc = useQueryClient();
  return useMutation<
    ImportCommitResult,
    ApiClientError,
    { file: File; duplicateAction: ImportDuplicateAction }
  >({
    mutationFn: ({ file, duplicateAction }) => commitProductServiceImport(file, duplicateAction),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Bulk "Add to / Remove from WhatsApp catalog". */
export function useSetProductsInWhatsAppCatalog() {
  const qc = useQueryClient();
  return useMutation<
    { updated: number },
    ApiClientError,
    { productServiceIds: string[]; include: boolean }
  >({
    mutationFn: (input) =>
      apiClient.post<{ updated: number }>('/products-services/whatsapp-catalog', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['whatsapp-catalog'] });
    },
  });
}

/** Categories in use (A-Z, with item counts) - the form's suggestions and the grid filter. */
export function useProductCategories() {
  return useQuery<{ name: string; count: number }[], ApiClientError>({
    queryKey: [...KEY, 'categories'],
    queryFn: () =>
      apiClient.get<{ name: string; count: number }[]>('/products-services/categories'),
  });
}

export function useProductsServices(query: ListProductsServicesQuery = {}) {
  return useQuery<ProductServiceListPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listProductsServices(query),
    // Keep showing the current page while the next page / filter loads.
    placeholderData: keepPreviousData,
  });
}

/** All / Products / Services counts under the same filters (grid tabs). */
export function useProductServiceCounts(query: Omit<ListProductsServicesQuery, 'kind'>) {
  const { page: _p, pageSize: _ps, sortBy: _s, sortOrder: _o, ...filters } = query;
  return useQuery<ProductServiceCounts, ApiClientError>({
    queryKey: [...KEY, 'counts', filters],
    queryFn: () =>
      apiClient.get<ProductServiceCounts>('/products-services/counts', {
        query: toQuery(filters),
      }),
    placeholderData: keepPreviousData,
  });
}

export function useProductService(id: string | undefined) {
  return useQuery<ProductServiceResource, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getProductService(id!),
    enabled: Boolean(id),
  });
}

export function useCreateProductService() {
  const qc = useQueryClient();
  return useMutation<ProductServiceResource, ApiClientError, CreateProductServiceInput>({
    mutationFn: createProductService,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateProductService(id: string) {
  const qc = useQueryClient();
  return useMutation<ProductServiceResource, ApiClientError, UpdateProductServiceInput>({
    mutationFn: (patch) => updateProductService(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteProductService() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteProductService,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUnarchiveProductService() {
  const qc = useQueryClient();
  return useMutation<
    ProductServiceResource,
    ApiClientError,
    Pick<ProductServiceResource, 'id' | 'kind'>
  >({
    mutationFn: unarchiveProductService,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// --- AI-assisted catalog import (photo/PDF of a supplier price list) ---

/**
 * AI extraction over the uploaded photo/PDF is too slow to hold the
 * browser request open for, so the backend now runs it in the background
 * and returns a job immediately (`202`) instead of the preview directly.
 * Poll `useCatalogImportJob`/`useCatalogImportJobs` for progress and the
 * eventual result.
 */
export const createCatalogImportJob = (file: File) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiClient.post<CatalogImportJob>('/products-services/ai-import/preview', form);
};

export const confirmCatalogImport = (draftId: string, input: CatalogImportConfirmInput) =>
  apiClient.post<CatalogImportConfirmResult>(
    `/products-services/ai-import/${draftId}/confirm`,
    input,
  );

export function useCreateCatalogImportJob() {
  const qc = useQueryClient();
  return useMutation<CatalogImportJob, ApiClientError, File>({
    mutationFn: createCatalogImportJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: CATALOG_IMPORT_JOBS_KEY }),
  });
}

function isCatalogImportJobOngoing(job: CatalogImportJob): boolean {
  return job.status === 'PENDING' || job.status === 'PROCESSING';
}

export const listCatalogImportJobs = async (): Promise<CatalogImportJob[]> =>
  (
    await apiClient.getCollection<CatalogImportJob>('/products-services/ai-import/jobs', {
      query: { pageSize: 20 },
    })
  ).data;

/**
 * The "ongoing imports" progress panel - polls only while at least one job
 * is still running. `enabled` defaults to true; pass `false` for a caller
 * without `products.manage` (the route 403s otherwise - same guard
 * `useNotifications`'s `open` param uses for its own permission-gated
 * fetch).
 */
export function useCatalogImportJobs(enabled = true) {
  return useQuery<CatalogImportJob[], ApiClientError>({
    queryKey: CATALOG_IMPORT_JOBS_KEY,
    queryFn: listCatalogImportJobs,
    enabled,
    refetchInterval: (query) => {
      const jobs = query.state.data;
      return jobs && jobs.some(isCatalogImportJobOngoing)
        ? pollInterval(CATALOG_IMPORT_JOB_POLL_MS)()
        : false;
    },
  });
}

export const getCatalogImportJob = (jobId: string) =>
  apiClient.get<CatalogImportJob>(`/products-services/ai-import/jobs/${jobId}`);

/** A single job's own detail view (opened right after creating it) - same polling-until-terminal behavior as the list. */
export function useCatalogImportJob(jobId: string | undefined) {
  return useQuery<CatalogImportJob, ApiClientError>({
    queryKey: [...CATALOG_IMPORT_JOBS_KEY, jobId],
    queryFn: () => getCatalogImportJob(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      query.state.data && isCatalogImportJobOngoing(query.state.data)
        ? pollInterval(CATALOG_IMPORT_JOB_POLL_MS)()
        : false,
  });
}

export function useConfirmCatalogImport() {
  const qc = useQueryClient();
  return useMutation<
    CatalogImportConfirmResult,
    ApiClientError,
    { draftId: string; input: CatalogImportConfirmInput }
  >({
    mutationFn: ({ draftId, input }) => confirmCatalogImport(draftId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
